// src/components/Header.js

import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiBell, FiLogOut, FiMoon, FiSun, FiInbox } from 'react-icons/fi';
import moment from 'moment';
import logo from '../assets/Service-manager.png';
import { AuthContext } from '../context/AuthContext';
import SearchBar from './SearchBar'; // ✅ Import new component

const Header = ({
    onLogout, isDarkMode, toggleDarkMode, notifications, markNotificationsAsRead,
    clearAllNotifications, tickets, communities, technicians, 
    inventory, sites 
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);

  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const hasUnreadNotifications = notifications.some(n => !n.read);

  const handleToggleNotifications = () => {
      const willBeOpen = !isNotificationsOpen;
      setIsNotificationsOpen(willBeOpen);
      if (willBeOpen) {
           markNotificationsAsRead();
      }
  };

  const handleNotificationClick = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
    setIsNotificationsOpen(false);
  };

  const requestNotificationPermission = () => {
    if (!('Notification' in window)) return;
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        try {
            const audio = new Audio('/sounds/notification.mp3');
            audio.volume = 0.5;
            audio.play();
        } catch(e) {}
        new Notification("Notifications Enabled", {
          body: "You will now receive alerts for new tickets.",
          icon: '/favicon.ico'
        });
      } else {
        alert('Notifications were denied.');
      }
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const userInitial = user && user.name ? user.name.charAt(0).toUpperCase() : '?';

  return (
    <header className="header">
      <div className="header-left">
        <Link to="/" title="Go to Dashboard">
          <img src={logo} alt="Ramco Service Management Logo" className="header-logo" />
        </Link>
      </div>

      {/* ✅ New Component: Logic extracted here */}
      <SearchBar 
        tickets={tickets}
        communities={communities}
        technicians={technicians}
        inventory={inventory}
        sites={sites}
      />

      <div className="header-actions">
        <div className="notification-btn-wrapper" ref={notificationsRef}>
            <button className="notification-btn" title="Notifications" onClick={handleToggleNotifications}>
                <FiBell size={20} />
                {hasUnreadNotifications && <span className="notification-indicator"></span>}
            </button>
            {isNotificationsOpen && (
                <div className="notifications-dropdown">
                    <div className="notifications-header">
                        <h3>Notifications</h3>
                        {notifications.length > 0 && (
                            <button onClick={clearAllNotifications} className="notification-clear-btn">
                                Clear All
                            </button>
                        )}
                    </div>
                    <div className="notifications-list">
                        {notifications.length > 0 ? (
                            notifications.map(notif => (
                                <div key={notif.id} className="notification-item" onClick={() => handleNotificationClick(notif.ticketId)}>
                                    <p className="notification-message">{notif.message}</p>
                                    <span className="notification-timestamp">
                                        {moment(notif.timestamp).fromNow()}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="notifications-empty">
                                <FiInbox size={32} />
                                <p>You're all caught up!</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>

        <div className="user-menu" ref={dropdownRef}>
          <div
            className="user-avatar-container"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            title={user ? user.name : 'User Menu'}
          >
            {user && user.profilePictureUrl ? (
              <img src={user.profilePictureUrl} alt={user.name} className="user-avatar" />
            ) : (
              <div className="user-avatar">
                {userInitial}
              </div>
            )}
          </div>

          {isDropdownOpen && (
            <div className="user-dropdown">
                {('Notification' in window) && Notification.permission !== 'granted' && (
                  <button onClick={requestNotificationPermission} className="dropdown-item">
                    <FiBell size={18} />
                    <span>Enable Notifications</span>
                  </button>
                )}

              <button
                onClick={toggleDarkMode}
                className="dropdown-item"
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDarkMode ? <FiSun size={18} /> : <FiMoon size={18} />}
                <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
              </button>

              <button onClick={onLogout} className="dropdown-item" title="Logout">
                <FiLogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;